import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { X, GripVertical, Star, Loader2, Plus } from 'lucide-react';

// Individual sortable image item
const SortableImage = ({ id, image, index, isMain, onRemove, onSetMain }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative aspect-square rounded-lg overflow-hidden border-2 ${
        isMain ? 'border-red-500 ring-2 ring-red-200' : 'border-transparent'
      } ${isDragging ? 'shadow-2xl scale-105' : 'hover:border-red-300'} transition-all bg-white`}
    >
      <img
        src={image}
        alt={`Property ${index + 1}`}
        className="w-full h-full object-cover"
        draggable={false}
      />
      
      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 p-1.5 bg-black/60 text-white rounded-md cursor-grab active:cursor-grabbing hover:bg-black/80 transition-colors"
        title="Drag to reorder"
      >
        <GripVertical className="w-4 h-4" />
      </div>

      {/* Remove button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(index);
        }}
        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
        title="Remove image"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Set as main button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onSetMain(index);
        }}
        className={`absolute bottom-2 left-2 px-2 py-1 rounded-md text-xs font-medium flex items-center gap-1 transition-colors ${
          isMain
            ? 'bg-red-500 text-white'
            : 'bg-black/60 text-white hover:bg-black/80'
        }`}
        title={isMain ? 'Main image' : 'Set as main image'}
      >
        <Star className={`w-3 h-3 ${isMain ? 'fill-white' : ''}`} />
        {isMain ? 'Main' : 'Set Main'}
      </button>

      {/* Position indicator */}
      <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-md">
        #{index + 1}
      </div>
    </div>
  );
};

// Main sortable image grid component
const SortableImageGrid = ({
  images,
  onImagesChange,
  mainImageIndex,
  onMainImageChange,
  maxImages = 50,
  minImages = 4,
  uploading = false,
  onUpload,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = images.findIndex((_, i) => `image-${i}` === active.id);
      const newIndex = images.findIndex((_, i) => `image-${i}` === over.id);

      // Reorder images
      const newImages = arrayMove(images, oldIndex, newIndex);
      onImagesChange(newImages);

      // Update main image index if it was moved
      if (oldIndex === mainImageIndex) {
        onMainImageChange(newIndex);
      } else if (oldIndex < mainImageIndex && newIndex >= mainImageIndex) {
        onMainImageChange(mainImageIndex - 1);
      } else if (oldIndex > mainImageIndex && newIndex <= mainImageIndex) {
        onMainImageChange(mainImageIndex + 1);
      }
    }
  };

  const handleRemove = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);

    // Adjust main image index
    if (index === mainImageIndex) {
      onMainImageChange(0);
    } else if (index < mainImageIndex) {
      onMainImageChange(mainImageIndex - 1);
    }
  };

  const handleSetMain = (index) => {
    onMainImageChange(index);
  };

  return (
    <div className="space-y-4">
      {/* Instructions */}
      <div className="flex items-center gap-2 text-sm text-gray-500 bg-blue-50 p-3 rounded-lg border border-blue-100">
        <GripVertical className="w-4 h-4 text-blue-500 flex-shrink-0" />
        <span>
          <strong>Drag images</strong> to reorder. The first image (or one marked with{' '}
          <Star className="w-3 h-3 inline fill-red-500 text-red-500" />) will be shown as the cover photo.
          <span className="text-gray-400 ml-1">(Max 10MB per image)</span>
        </span>
      </div>

      {/* Image grid */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={images.map((_, i) => `image-${i}`)}
          strategy={rectSortingStrategy}
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {images.map((image, index) => (
              <SortableImage
                key={`image-${index}`}
                id={`image-${index}`}
                image={image}
                index={index}
                isMain={index === mainImageIndex}
                onRemove={handleRemove}
                onSetMain={handleSetMain}
              />
            ))}

            {/* Upload button */}
            {images.length < maxImages && (
              <label className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-red-400 hover:bg-red-50 transition-colors">
                {uploading ? (
                  <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                ) : (
                  <>
                    <Plus className="w-8 h-8 text-gray-400" />
                    <span className="text-sm text-gray-500 mt-2">Add Image</span>
                    <span className="text-xs text-gray-400 mt-1">
                      {images.length}/{maxImages}
                    </span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onUpload}
                  disabled={uploading}
                  data-testid="property-image-upload"
                />
              </label>
            )}
          </div>
        </SortableContext>
      </DndContext>

      {/* Validation messages */}
      {images.length > 0 && images.length < minImages && (
        <p className="text-sm text-orange-500">
          Please upload at least {minImages - images.length} more image(s). Minimum {minImages} images required.
        </p>
      )}

      {/* Image count summary */}
      {images.length > 0 && (
        <p className="text-sm text-gray-500">
          {images.length} of {maxImages} images uploaded
          {images.length >= minImages && ' ✓'}
        </p>
      )}
    </div>
  );
};

export default SortableImageGrid;
